import type { EntitlementTier } from "./tiers";

/**
 * The KEY NAMES of every string this phase shows a learner about money.
 *
 * This module holds no copy. It holds the SHAPE of the copy: which keys form a
 * compliance-mandated set, in which order, and how many of them there are. The
 * words live in `src/i18n/messages/{en,es,pt-BR}.json`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS AT ALL, RATHER THAN RENDERERS NAMING KEYS DIRECTLY.
 *
 * CMP-40 says five facts must appear in visual proximity to the purchase
 * control: the price, the billing period, the first renewal date, that the
 * subscription renews automatically until cancelled, and how to cancel. A page
 * that renders four of those satisfies any test written as prose, because prose
 * cannot count. So the five are an ORDERED TUPLE, every renderer maps it, and a
 * fact dropped from a render fails a test that was never edited.
 *
 * The same reasoning covers CMP-42's cancellation instructions, which the
 * requirement words as step-by-step: they are a tuple, in step order, and an
 * email template that inlines two of three steps fails the count.
 *
 * There is a second, quieter reason. The catalogs are typed `typeof en`, so
 * `tsc` catches a key MISSING from es or pt-BR and says nothing at all about a
 * key that exists in all three and is still the English text, or still the
 * PREVIOUS English text after a rewording. These are payment disclosures and
 * `pt-BR` is a launch cell, so a stale string here is a compliance gap in the
 * market the ramp is aimed at. `src/test/lib/billing/copy.test.ts` closes the
 * half `tsc` cannot see, and it can only do so over a closed set of keys.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NO PRICE, PERIOD OR DATE IS EVER A LITERAL.
 *
 * D-04 gives iOS a HIGHER price than the web, to offset the platform's cut. One
 * hardcoded number is therefore wrong on one of the two surfaces from the moment
 * it is typed. A date is worse than wrong: the first charge falls at the end of
 * D-03's fourteen-day trial, computed at purchase time, and a literal date in a
 * catalog becomes false silently and stays false. Every amount, every period and
 * every date in this copy is an interpolation placeholder filled by the caller.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS MODULE IMPORTS ONLY THE TIER VOCABULARY.
 *
 * It is read by the subscribe page's client component and by the iOS tree across
 * the shared-lib purity seam plan 34-01 built. A single import of `@lib/prisma`,
 * `@lib/api/*` or `@lib/config/*` reaches `server-only`, and the symptom is a
 * broken WEB BUILD rather than a red unit test. `tiers.ts` itself imports
 * nothing, which is what makes it the one safe dependency. A source-reading case
 * in the test file enforces this, because nothing else can.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * CMP-40's five facts, IN THE ORDER THEY ARE READ.
 *
 * The order is part of the contract and not a formatting preference: a learner
 * meets the amount, then how often, then when the first one lands, then that it
 * keeps happening, then how to stop it. Renderers map this tuple; none of them
 * writes five elements by hand.
 *
 * `as const` is load-bearing twice over. It makes the tuple's LENGTH part of the
 * type, so a sixth fact is a type change rather than only a JSON edit, and it
 * makes each member a literal type, so a renderer's exhaustive map is checked.
 */
export const RENEWAL_DISCLOSURE_KEYS = [
  /** The amount. Interpolates {price}, which differs by origin under D-04. */
  "billing.renewal.price",
  /** How often. Interpolates {period}, itself a translated word from PERIOD_LABEL_KEYS. */
  "billing.renewal.period",
  /** When the first charge lands. Interpolates {date}, computed from the trial length. */
  "billing.renewal.firstRenewal",
  /** That it keeps happening until cancelled. Interpolates {price} and {period}. */
  "billing.renewal.automatic",
  /** How to stop it. No placeholder: it names a place in the app, not a number. */
  "billing.renewal.cancellation",
] as const;

/**
 * CMP-41's TWO consents, which are two because the requirement says so.
 *
 * The auto-renewal agreement must be a SEPARATE AFFIRMATIVE ACTION, distinct
 * from accepting the Terms of Service. One combined checkbox does not satisfy it,
 * and neither does one label carrying both sentences. That is why these are two
 * keys rather than one, and why the test asserts that neither string mentions the
 * other's subject.
 *
 * This pair is also the entire reason a Convo-owned pre-checkout surface exists:
 * the processor's hosted page has exactly one consent checkbox and it is bound to
 * the Terms, with no second one available at any price.
 */
export const CONSENT_LABEL_KEYS = {
  terms: "billing.consent.terms",
  autoRenewal: "billing.consent.autoRenewal",
} as const;

/**
 * The two documents CMP-41's Terms consent refers to, and the sentence shown
 * while neither exists.
 *
 * WHY THE PURCHASE SCREEN NEEDS THESE AT ALL. `billing.consent.terms` says "I
 * have read and accept the Terms of Service", and until 2026-09-05 there was no
 * Terms of Service anywhere - no document, no URL, no link on the screen. The
 * toggle asked for an attestation nobody could truthfully make, and every case
 * around it stayed green because they ask whether the two consents are separate
 * and unticked, not whether the thing being consented to exists.
 *
 * Apple 3.1.2 wants functional links to BOTH the terms and the privacy policy
 * on the purchase screen, which is why `unavailable` names the pair rather than
 * the terms alone. It renders while either URL is still the checked-in
 * placeholder, and the screen refuses to sell for as long as it does.
 *
 * The URLs themselves are NOT copy and are not here: they live in
 * `src/lib/legal.ts` beside the check that reads them, and filling them in is
 * `OP-IAP-10`. They moved out of `src/ios/src/lib/billing/legal.ts` on
 * 2026-09-08 - that module re-exports now - because CMP-45 needs both documents
 * reachable before signup on BOTH clients, and one definition is one edit on
 * the day counsel publishes them.
 */
export const LEGAL_DOCUMENT_KEYS = {
  terms: "billing.legal.terms",
  privacy: "billing.legal.privacy",
  unavailable: "billing.legal.unavailable",
} as const;

/**
 * The billing period as a TRANSLATED WORD, so "every {period}" reads correctly
 * in three languages.
 *
 * Without this the caller would have to pass the English word "month" into a
 * Portuguese sentence, which is exactly the kind of half-translation that passes
 * every key-parity check ever written.
 */
export const PERIOD_LABEL_KEYS = {
  month: "billing.period.month",
  year: "billing.period.year",
} as const;

/**
 * One display label per tier, keyed off the tier union rather than off a free
 * string.
 *
 * Typed `Record<EntitlementTier, string>` on purpose: a fourth tier added to
 * `ENTITLEMENT_TIERS` fails to COMPILE here until it has a label, rather than
 * rendering a dotted key on the page where a learner decides whether to pay.
 *
 * The labels describe what the tier buys in D-08's terms, because the gate line
 * and the subscribe page both spend them and the learner must read the same
 * description of the split in both places: `basic` is text tutoring, `full` adds
 * voice. Voice is the paid-up differentiator and it is also the expensive half
 * per learner, which is why the split falls there.
 */
export const TIER_LABEL_KEYS: Record<EntitlementTier, string> = {
  none: "billing.tier.none",
  basic: "billing.tier.basic",
  full: "billing.tier.full",
};

/**
 * The subscribe surface: its heading, its trial explanation, its submit control,
 * the three disabled-state hints, its failure state, and the label of the link
 * that leads here from elsewhere.
 *
 * There are THREE disabled hints and not one because the hint has to name WHICH
 * agreement is still missing. A single "tick the boxes" hint is unusable to a
 * learner who has ticked one, and it also quietly re-merges the two consents that
 * CMP-41 requires to stay apart.
 *
 * `trial` interpolates {days} rather than stating fourteen. D-03's trial length
 * is a constant the checkout session is built from, and a catalog that states it
 * independently is a second source of truth for a number that appears on an
 * invoice.
 */
export const SUBSCRIBE_PAGE_KEYS = {
  heading: "billing.subscribe.heading",
  trial: "billing.subscribe.trial",
  submit: "billing.subscribe.submit",
  needsTerms: "billing.subscribe.needsTerms",
  needsAutoRenewal: "billing.subscribe.needsAutoRenewal",
  needsBoth: "billing.subscribe.needsBoth",
  error: "billing.subscribe.error",
  link: "billing.subscribe.link",
  loading: "billing.subscribe.loading",
  unavailable: "billing.subscribe.unavailable",
  retry: "billing.subscribe.retry",
  confirmed: "billing.subscribe.confirmed",
  confirming: "billing.subscribe.confirming",
} as const;

/** Purchase restoration states shown by the native account and purchase screens. */
export const RESTORE_PURCHASE_KEYS = {
  control: "billing.restore.control",
  busy: "billing.restore.busy",
  success: "billing.restore.success",
  none: "billing.restore.none",
  error: "billing.restore.error",
} as const;

/**
 * CMP-42's cancellation instructions, as an ORDERED TUPLE of steps.
 *
 * The requirement's own words are step-by-step instructions, so the steps are
 * separate keys rather than one string with newlines. A single blob renders
 * identically whether it holds three steps or two, cannot be numbered by a
 * template, and cannot be counted by a test. Templates map this tuple.
 */
export const CANCELLATION_STEP_KEYS = [
  "billing.cancellation.step1",
  "billing.cancellation.step2",
  "billing.cancellation.step3",
] as const;

/**
 * CMP-42's acknowledgment email, sent at purchase.
 *
 * The requirement names what the acknowledgment must CONTAIN: the renewal terms
 * restated, the cancellation policy, and the step-by-step cancellation
 * instructions. The steps come from `CANCELLATION_STEP_KEYS` above, which is why
 * they are not repeated here; `cancellationHeading` and `cancellationPolicy`
 * deliberately point into the shared `billing.cancellation` namespace so the
 * policy a learner reads in the email is byte-identical to the one on the account
 * screen. Two separately authored policies would drift on the first edit.
 *
 * `renewalTerms` carries {price} and {date} and DELIBERATELY NOT {period}.
 *
 * The period is not available to the sender. `appleCompliance.ts` rules that the
 * price and billing period "must be read from the StoreKit product rather than
 * from a checked-in literal, because App Store Connect is where they are
 * actually set" - and that is a client-side fact. The App Store Server API does
 * not carry it: `JWSTransactionDecodedPayload` has `offerPeriod`, which is a
 * promotional offer's period rather than the base term, and
 * `JWSRenewalInfoDecodedPayload` has `renewalPrice` and `autoRenewProductId` and
 * no period at all. Checked against `@apple/app-store-server-library@3.1.0`'s
 * own types on 2026-09-07.
 *
 * So the copy states what the sender KNOWS to be true - the amount, the date of
 * the first charge, that it renews automatically at that price, and that it
 * continues until cancelled. Interpolating a period the server had to guess
 * would put a number in a legal disclosure that nothing verifies, which is worse
 * than omitting it. Owner decision 2026-09-07, taken after the App Store Server
 * API option was checked and found not to answer.
 */
export const ACK_EMAIL_KEYS = {
  subject: "billing.ack.subject",
  greeting: "billing.ack.greeting",
  planHeading: "billing.ack.planHeading",
  renewalTerms: "billing.ack.renewalTerms",
  cancellationHeading: "billing.cancellation.heading",
  cancellationPolicy: "billing.cancellation.policy",
  closing: "billing.ack.closing",
} as const;

/**
 * CMP-44's reminder before the first charge that follows a free trial.
 *
 * `body` names the deadline and the amount; `cancelBefore` names the deadline
 * again beside the action, which is what makes it a reminder rather than an
 * announcement - the learner is being warned about money, and a warning they
 * cannot act on is a notification.
 *
 * `body` CARRIES {price} AND {date} AND DELIBERATELY NOT {period}, for the
 * reason recorded on ACK_EMAIL_KEYS: no server-side Apple source carries the
 * base subscription term. The {price} here is Apple's `renewalInfo.renewalPrice`
 * and NOT the transaction price, which for a free-trial purchase is zero; see
 * `User.trialRenewalPriceMilliunits` for why the column exists.
 */
export const TRIAL_REMINDER_KEYS = {
  subject: "billing.trialReminder.subject",
  greeting: "billing.trialReminder.greeting",
  body: "billing.trialReminder.body",
  cancelBefore: "billing.trialReminder.cancelBefore",
  cancellationHeading: "billing.cancellation.heading",
} as const;

/**
 * CMP-43's cancel control on the account screen, and the sentence beside it.
 *
 * `cancelDescription` is what makes the control safe to press: it says that
 * cancelling stops the NEXT charge and that access continues to the end of the
 * period already paid for. A cancel button with no such sentence reads as
 * "destroy my account now" and is the reason cancellation flows get abandoned.
 */
export const MANAGE_SUBSCRIPTION_KEYS = {
  heading: "billing.manage.heading",
  cancelControl: "billing.manage.cancelControl",
  cancelDescription: "billing.manage.cancelDescription",
  error: "billing.manage.error",
} as const;

/**
 * The entitlement gate's two strings, which live under `limits` rather than
 * `billing` and do so deliberately.
 *
 * A learner now has THREE ways for voice to be unavailable, and each means
 * something different: a spend bound reopens on a schedule the system knows, a
 * capability flag reopens when an operator decides, and a tier reopens when the
 * learner buys something. They sit in one namespace, beside `limits.voiceOff` and
 * `limits.voiceDisabledHint`, so the next person adding a refusal reads all three
 * before inventing a fourth visual language for unavailability.
 *
 * NEITHER STRING NAMES A DESTINATION. The web surface wraps the line in a link to
 * the subscribe page; the native one must not, because steering a learner from
 * inside the app toward the cheaper surface is what the platform does not permit
 * under D-04. Keeping the destination out of the copy is what lets one pair of
 * strings serve both clients honestly.
 *
 * These two names are restated as constants in `@lib/billing/refusalClient` for
 * the client seam. The restatement is deliberate and the two are pinned equal by
 * a test rather than by an import, which is the same anti-drift trade that module
 * already makes for its wire code.
 */
export const ENTITLEMENT_GATE_KEYS = {
  line: "limits.entitlementOff",
  hint: "limits.entitlementDisabledHint",
} as const;
